import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const CART_KEY = "ps_cart";

// ---------- AUTH ----------
const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip if returning from Google OAuth
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ps_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name, accept_terms: true });
    localStorage.setItem("ps_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    localStorage.removeItem("ps_token");
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh: checkAuth, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
};
export const useAuth = () => useContext(AuthCtx);

// ---------- CART ----------
const CartCtx = createContext(null);

const readCart = () => {
  try { return JSON.parse(localStorage.getItem(CART_KEY) || "[]"); } catch { return []; }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(readCart());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.product_id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.product_id ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.product_id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          language: product.language,
          category: product.category,
          is_preorder: product.is_preorder,
          preorder_type: product.preorder_type || null,
          deposit_percent: product.deposit_percent || null,
          image_url: product.image_url || null,
          qty,
        },
      ];
    });
    setOpen(true);
  };

  const remove = (product_id) => setItems((p) => p.filter((i) => i.product_id !== product_id));
  const updateQty = (product_id, qty) =>
    setItems((p) => p.map((i) => (i.product_id === product_id ? { ...i, qty: Math.max(1, qty) } : i)));
  const clear = () => setItems([]);

  const effectivePrice = (i) =>
    i.preorder_type === "allocation-deposit" && i.deposit_percent
      ? (i.price * i.deposit_percent) / 100
      : i.price;

  const subtotal = items.reduce((s, i) => s + effectivePrice(i) * i.qty, 0);
  const balance = items.reduce(
    (s, i) => s + (i.preorder_type === "allocation-deposit" && i.deposit_percent
      ? (i.price * (100 - i.deposit_percent)) / 100 * i.qty
      : 0),
    0
  );
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartCtx.Provider value={{ items, add, remove, updateQty, clear, subtotal, balance, count, open, setOpen, effectivePrice }}>
      {children}
    </CartCtx.Provider>
  );
};
export const useCart = () => useContext(CartCtx);

// ---------- WISHLIST (local, syncs when logged) ----------
const WL_KEY = "ps_wishlist";
const WishlistCtx = createContext(null);

export const WishlistProvider = ({ children }) => {
  const [ids, setIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(WL_KEY) || "[]"); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem(WL_KEY, JSON.stringify(ids)); }, [ids]);

  const toggle = (product_id) => {
    setIds((prev) => (prev.includes(product_id) ? prev.filter((x) => x !== product_id) : [...prev, product_id]));
    // Best-effort server sync (silent)
    api.post(`/wishlist/${product_id}`).catch(() => {});
  };
  const has = (product_id) => ids.includes(product_id);

  return (
    <WishlistCtx.Provider value={{ ids, toggle, has, count: ids.length }}>
      {children}
    </WishlistCtx.Provider>
  );
};
export const useWishlist = () => useContext(WishlistCtx);
